import type { ChangeEvent, ClipboardEvent, FC, FocusEvent, KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import cn from 'classnames';
import { _isNotBlank, _notNil } from '@/littledash';
import styles from './TagsInput.module.scss';
import { RiCloseLine } from 'react-icons/ri';

interface TagsInputProps {
  value: Array<string>;
  onChange: (tags: Array<string>) => void;
  placeholder?: string;
  maxTags?: number;
  onlyUnique?: boolean;
  validate?: (tag: string) => boolean;
  className?: string;
  disabled?: boolean;
  testId?: string;
  /**
   * When true, renders the input inline with the tag chips (single wrapping row,
   * clicking anywhere on the wrapper focuses the input). When false (default),
   * stacks the input below the tag list.
   */
  insideTagsInput?: boolean;
}

interface TagProps {
  name: string;
  index: number;
  onDelete: (index: number, event: MouseEvent) => void;
  disabled?: boolean;
}

const Tag: FC<TagProps> = ({ name, index, onDelete, disabled = false }) => (
  <div className={styles.tag}>
    {name}
    {!disabled && (
      <a
        type="button"
        className={styles.tagRemove}
        aria-label={`Remove tag ${name}`}
        onClick={(event) => {
          onDelete(index, event);
        }}
      >
        <RiCloseLine size={20} />
      </a>
    )}
  </div>
);

const TAG_DELIMITER_PATTERN = /[,;\n\r\t]+/;

/**
 * Editable list of string tags. Users can add new tags by typing and pressing
 * Enter / Comma / Tab / blur / paste, and remove tags via Backspace (when input is empty) or
 * the per-tag delete button.
 *
 * Layout is controlled by `insideTagsInput`:
 * - `false` (default): tag chips render in a list above the input.
 * - `true`: tag chips and input wrap together on a single row, and clicking
 *   the wrapper focuses the input.
 */
const TagsInput: FC<TagsInputProps> = ({
  value,
  onChange,
  placeholder = 'Separate tags with a comma',
  maxTags,
  onlyUnique = true,
  validate,
  className,
  disabled = false,
  testId = 'tags-input',
  insideTagsInput = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isTagAllowed = useCallback(
    (tag: string, existingTags: Array<string>): boolean => {
      if (!_isNotBlank(tag)) {
        return false;
      }
      if (_notNil(validate) && !validate(tag)) {
        return false;
      }
      if (onlyUnique && existingTags.includes(tag)) {
        return false;
      }
      if (_notNil(maxTags) && existingTags.length >= maxTags) {
        return false;
      }
      return true;
    },
    [onlyUnique, maxTags, validate]
  );

  const appendTags = useCallback(
    (rawTags: Array<string>, refocus = true): void => {
      const nextTags = rawTags.reduce<Array<string>>(
        (acc, rawTag) => {
          const trimmed = rawTag.trim();
          if (isTagAllowed(trimmed, acc)) {
            acc.push(trimmed);
          }
          return acc;
        },
        [...value]
      );

      if (nextTags.length !== value.length) {
        onChange(nextTags);
      }
      setInputValue('');
      if (refocus) {
        setTimeout(() => {
          const input = inputRef.current;
          if (_notNil(input)) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
          }
        }, 0);
      }
    },
    [isTagAllowed, onChange, value]
  );

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const nextValue = event.target.value === ',' ? '' : event.target.value;
    setInputValue(nextValue);
  }, []);

  const addTag = useCallback(
    (tag: string, refocus = true): void => {
      appendTags([tag], refocus);
    },
    [appendTags]
  );

  const handleTagDelete = useCallback(
    (index: number): void => {
      const nextTags = value.filter((_, currentIndex) => currentIndex !== index);
      onChange(nextTags);
    },
    [onChange, value]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      const { key, currentTarget } = event;
      if (key === 'Enter' || key === ',' || key === 'Tab') {
        const candidate = currentTarget.value.trim();
        if (_isNotBlank(candidate)) {
          event.preventDefault();
          addTag(candidate);
        }
        return;
      }
      if (key === 'Backspace' && !_isNotBlank(currentTarget.value) && value.length > 0) {
        onChange(value.slice(0, -1));
      }
    },
    [addTag, onChange, value]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>): void => {
      const candidate = event.currentTarget.value.trim();
      if (_isNotBlank(candidate)) {
        addTag(candidate, false);
      }
    },
    [addTag]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>): void => {
      const pastedText = event.clipboardData.getData('text');
      if (!_isNotBlank(pastedText) || !TAG_DELIMITER_PATTERN.test(pastedText)) {
        return;
      }
      event.preventDefault();
      const combinedText = `${event.currentTarget.value}${pastedText}`;
      const candidates = combinedText.split(TAG_DELIMITER_PATTERN);
      appendTags(candidates);
    },
    [appendTags]
  );

  const handleWrapperClick = useCallback((): void => {
    if (insideTagsInput && !disabled && _notNil(inputRef.current)) {
      inputRef.current.focus();
    }
  }, [insideTagsInput, disabled]);

  const inputElement = (
    <input
      ref={inputRef}
      type="text"
      name="tagsInput"
      className={styles.tagsInput}
      placeholder={insideTagsInput && value.length > 0 ? '' : placeholder}
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onPaste={handlePaste}
      disabled={disabled}
      data-test-element="input"
    />
  );

  return (
    <div
      ref={wrapperRef}
      className={cn(styles.tagsInputWrapper, className, { [styles.insideTagsInput]: insideTagsInput })}
      data-test-component="TagsInput"
      data-testid={testId}
      onClick={handleWrapperClick}
    >
      <div className={styles.tagsList} data-test-element="tags-list">
        {value.map((tag, index) => (
          <Tag key={`${tag}-${index}`} name={tag} onDelete={handleTagDelete} index={index} disabled={disabled} />
        ))}
        {insideTagsInput && inputElement}
      </div>
      {!insideTagsInput && inputElement}
    </div>
  );
};

export default TagsInput;
